import Axios from 'axios';
import hmacSHA512 from 'crypto-js/hmac-sha512';
import SHA512 from 'crypto-js/sha512';
import { isApplicationResponse } from './is-application-response';
import { isUserResponse, isUserSettingsResponse } from './is-user-response';
import { keysToSnake, keysToCamel } from './utils';
let activeJWT = '';
let serviceConfig = {};
export const httpClient = Axios.create({
    headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
    },
    withCredentials: true,
});
export const getJWT = () => {
    return activeJWT;
};
export const changeJWT = (newJwt) => {
    activeJWT = newJwt;
};
export const isRequiredJWT = (requestUrl) => {
    var _a;
    if (requestUrl && ['/login', '/token'].includes(requestUrl)) {
        return false;
    }
    return !((_a = serviceConfig.urlsWithoutJWT) === null || _a === void 0 ? void 0 : _a.some(url => requestUrl === null || requestUrl === void 0 ? void 0 : requestUrl.startsWith(url)));
};
export const signPayload = (secret, timestamp, hashedPayload, method, path) => {
    const data = secret + timestamp + hashedPayload + method + path;
    return hmacSHA512(data, secret).toString();
};
export const hashPayload = (data) => {
    var _a;
    const serializedData = (_a = JSON.stringify(data)) !== null && _a !== void 0 ? _a : '';
    return SHA512(serializedData).toString();
};
export const getPathname = (baseUrl, url) => {
    return new URL((baseUrl || location.origin) + (url || ''), location.origin).pathname;
};
export const updateConfiguration = (config) => {
    serviceConfig.baseUrl = config.baseUrl;
    serviceConfig.urlsWithoutJWT = config.urlsWithoutJWT;
};
export const setUpHttpClientConfig = (config) => {
    serviceConfig = Object.assign({}, config);
    httpClient.interceptors.request.use(async (request) => {
        var _a;
        request.baseURL = request.baseURL || serviceConfig.baseUrl;
        request.headers = Object.assign(Object.assign({}, request.headers), { 'Accept-Language': serviceConfig.lang });
        if (isRequiredJWT(request.url) && activeJWT) {
            request.headers.Authorization = `Bearer ${activeJWT}`;
        }
        request.data = keysToSnake(request.data);
        const timestamp = Math.floor(Date.now() / 1000).toString();
        const path = getPathname(request === null || request === void 0 ? void 0 : request.baseURL, request === null || request === void 0 ? void 0 : request.url);
        const hashedContent = hashPayload(request.data);
        const method = ((_a = request.method) === null || _a === void 0 ? void 0 : _a.toUpperCase()) || 'GET';
        const searchParams = new URLSearchParams(window.location.search);
        if (searchParams.has('jur')) {
            // the query parameter should always take precedence. It's used in email templates
            request.headers['x-origin-country'] = searchParams.get('jur');
        }
        else if (!request.headers['x-origin-country'] && serviceConfig.country) {
            /** @see SGN-1612 fallback to the global country only if there was no override for this particular call */
            request.headers['x-origin-country'] = serviceConfig.country;
        }
        request.headers['api-key'] = serviceConfig.apiKey || '';
        request.headers['api-content-hash'] = hashedContent;
        request.headers['api-timestamp'] = timestamp;
        request.headers['api-signature'] = signPayload(serviceConfig.apiSecret || '', timestamp, hashedContent, method, path);
        return request;
    });
    httpClient.interceptors.response.use(response => {
        var _a;
        const eventBus = serviceConfig.eventBus;
        response.data = keysToCamel(response.data);
        if (isUserResponse(response) && eventBus) {
            eventBus.publishSync('http_client.user_changed', response.data);
        }
        else if (isUserSettingsResponse(response) && eventBus) {
            eventBus.publishSync('http_client.user_settings_changed', response.data);
        }
        else if (((_a = response.config.url) === null || _a === void 0 ? void 0 : _a.includes('logout')) && eventBus && response.status === 200) {
            /**
             * So far the `/logout` endpoint is the only way to manually dismiss the session
             * */
            eventBus.publishSync('http_client.user_changed', undefined);
        }
        else if (isApplicationResponse(response) && eventBus && response.status === 200) {
            /**
             * PATH request to /applications/:id returns a new application and it's a signal that we updated
             * the application for the app
             * */
            eventBus.publishSync('http_client.application_changed', response.data);
        }
        return response;
    }, async (error) => {
        var _a, _b, _c;
        if ((_a = error.response) === null || _a === void 0 ? void 0 : _a.data)
            error.response.data = keysToCamel(error.response.data);
        if (((_b = error.response) === null || _b === void 0 ? void 0 : _b.status) === 429) {
            serviceConfig.eventBus.publishSync('mfe.page-error', 429);
            throw error;
        }
        const unauthorizedCode = 401;
        if (((_c = error.response) === null || _c === void 0 ? void 0 : _c.status) === unauthorizedCode &&
            isRequiredJWT(error.config.url) &&
            //  401 status should not cause redirect for changing passport endpoint
            error.response.config.url !== '/user/password') {
            try {
                window.sessionStorage.setItem('nextRoute', location.pathname);
            }
            catch (e) { }
            // The best approach here is to keep the previous URL lang+country, but for the sake of code simplicity it
            // can be omitted because users anyway will be redirected to his previous lang+country settings
            location.href = '/signin';
        }
        throw error;
    });
};
